import * as Sentry from '@sentry/react';
import axios, { type AxiosRequestConfig } from 'axios';
import { apiFailure, sanitizeEvent, normalizePath } from './policy';

function selectedBackend(): string | undefined {
  try { return localStorage.getItem('schoolBackendURL') || undefined; } catch { return undefined; }
}

export function initErrorTracking() {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN || 'https://d69748c36565fecf43617a6aba67d86c@o4506778855473152.ingest.us.sentry.io/4508970053664768',
    enabled: import.meta.env.PROD || import.meta.env.VITE_SENTRY_ENABLED === 'true',
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || (import.meta.env.PROD ? 'production' : 'development'),
    sendDefaultPii: false,
    beforeSend: event => sanitizeEvent({ ...event, tags: { ...event.tags, component: 'frontend', 'dashboard.route': normalizePath(window.location.pathname) } }, selectedBackend()),
    beforeBreadcrumb: () => null,
  });
  // Login uses the default Axios client; dashboard requests use the school client.
  axios.interceptors.request.use(config => { prepareApiRequest(config); return config; });
  axios.interceptors.response.use(response => {
    reportApiFailure({ ...response.config, baseURL: response.config.baseURL || response.config.url }, response.status, Number(response.data?.status), undefined, response.headers['x-request-id']);
    return response;
  }, error => {
    reportApiFailure({ ...error.config, baseURL: error.config?.baseURL || error.config?.url }, error.response?.status, Number(error.response?.data?.status), error.code, error.response?.headers?.['x-request-id']);
    return Promise.reject(error);
  });
}

// Older school backends do not allow X-Request-ID in CORS. Only send it after
// a response advertises support; new backends generate IDs for first requests.
const capableOrigins = new Set<string>();
function apiOrigin(config: AxiosRequestConfig): string {
  try { return new URL(config.baseURL || config.url || '').origin; } catch { return ''; }
}
export function prepareApiRequest(config: AxiosRequestConfig) {
  if (capableOrigins.has(apiOrigin(config)) && config.headers && typeof globalThis.crypto?.randomUUID === 'function') {
    config.headers['X-Request-ID'] = crypto.randomUUID();
  }
}

// Bound repeated query retries without mixing schools or retaining payloads.
const recent = new Map<string, number>();
export function reportApiFailure(config: AxiosRequestConfig, httpStatus?: number, logicalStatus?: number, code?: string, responseRequestId?: string) {
  if (responseRequestId && /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i.test(responseRequestId)) {
    capableOrigins.add(apiOrigin(config));
  }
  const event = apiFailure(config, httpStatus, logicalStatus, code, responseRequestId);
  if (!event) return;
  const key = `${String(event.tags?.school)}:${event.fingerprint?.join(':')}`;
  const now = Date.now();
  if (now - (recent.get(key) || 0) < 60_000) return;
  if (recent.size >= 100) recent.delete(recent.keys().next().value!);
  recent.set(key, now);
  Sentry.captureEvent(event);
}

export function reportUiException(error: unknown) {
  Sentry.captureException(error, { tags: { 'error.source': 'react_boundary' } });
}
